import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Images, X } from "lucide-react";

// Fotografierna kommer fran Mats egna inskickade bilder.
// Lokala, optimerade WebP-filer gor att sidan inte beror pa delade Drive-lankar.
const base = "/assets/mats/";

const WORK = [
  {
    id: "lager-jamforelse",
    category: "stadning",
    title: "Lagerstädning – före och efter",
    description: "Samma lagergång före och efter städning, fotograferad från olika avstånd.",
    before: `${base}lager-fore-960.webp`,
    after: `${base}lager-efter-960.webp`,
    beforeAlt: "Lagergång med skräp och damm på betonggolvet mellan pallställen före städning",
    afterAlt: "Samma lagergång efter städning, med skräpet borttaget från betonggolvet",
    responsive: true,
    featured: true,
  },
  {
    id: "badrum-jamforelse",
    category: "stadning",
    title: "Badrumsstädning – före och efter",
    description: "Rengöring av kakel och golv i ett badrum.",
    before: `${base}IMG_2629.webp`,
    after: `${base}IMG_2631.webp`,
    featured: true,
  },
  { id: "tradgard-beskarning", category: "tradgard", title: "Trädgård efter beskärning", src: `${base}IMG_2499.webp`, featured: true },
  { id: "tradgard-jamforelse-1", category: "tradgard", title: "Trädgårdsarbete – två olika skeden", src: `${base}Resized_20260602_141457.webp` },
  { id: "arbete-hack", category: "tradgard", title: "Pågående trädgårdsarbete", src: `${base}IMG_2477.webp`, featured: true },
  { id: "tradgard-jamforelse-2", category: "tradgard", title: "Trädgårdsarbete – jämförelsebilder", src: `${base}Resized_20260602_141320.webp`, featured: true },
  { id: "tradgard-gras", category: "tradgard", title: "Trädgård och gräsyta", src: `${base}IMG_2498.webp`, featured: true },
  { id: "tradgard-trad-1", category: "tradgard", title: "Trädgård före röjning", src: `${base}IMG_2493.webp` },
  { id: "tradgard-trad-2", category: "tradgard", title: "Trädgård med buskar och träd", src: `${base}IMG_2494.webp` },
  { id: "arbete-buskar", category: "tradgard", title: "Klippning och röjning av växtlighet", src: `${base}IMG_2480.webp` },
  { id: "arbete-grenar", category: "tradgard", title: "Arbete med växter och buskar", src: `${base}IMG_2482.webp` },
  { id: "tradgard-jamforelse-3", category: "tradgard", title: "Trädgårdsarbete – jämförelsebilder", src: `${base}Resized_20260602_141708.webp` },
  { id: "badrum-kakel", category: "stadning", title: "Badrum före rengöring", src: `${base}IMG_2628.webp` },
  { id: "badrum-detalj", category: "stadning", title: "Detaljbild från badrumsstädning", src: `${base}IMG_2627.webp` },
  { id: "badrum-golv", category: "stadning", title: "Golv före rengöring", src: `${base}IMG_2630.webp` },
];

const FILTERS = [
  { id: "alla", label: "Alla" },
  { id: "tradgard", label: "Trädgård" },
  { id: "stadning", label: "Städning" },
];

function comparisonPhotos(item) {
  return [
    { src: item.before, label: "Före", alt: item.beforeAlt || `${item.title}, före` },
    { src: item.after, label: "Efter", alt: item.afterAlt || `${item.title}, efter` },
  ];
}

function imageVariants(item, src, sizes) {
  return item.responsive ? {
    srcSet: [480, 960, 1440].map((width) => `${src.replace("-960.webp", `-${width}.webp`)} ${width}w`).join(", "),
    sizes,
    width: 960,
    height: 1280,
  } : {};
}

function Photo({ item, className = "", loading = "lazy" }) {
  return item.before ? (
    <span className={`grid grid-cols-2 gap-0.5 overflow-hidden ${className}`}>
      {comparisonPhotos(item).map(({ src, label, alt }) => (
        <span key={label} className="relative min-h-0 min-w-0 overflow-hidden">
          <img src={src} alt={alt} {...imageVariants(item, src, "(min-width: 1152px) 175px, (min-width: 1024px) 15vw, (min-width: 640px) 23vw, 46vw")} loading={loading} decoding="async" className="h-full w-full object-cover object-center" />
          <span className="absolute left-2 top-2 rounded-md bg-white/95 px-2 py-1 text-[11px] font-bold text-[var(--ink)] shadow-sm sm:left-3 sm:top-3">{label}</span>
        </span>
      ))}
    </span>
  ) : (
    <img src={item.src} alt={item.title} loading={loading} className={`object-cover object-center ${className}`} />
  );
}

function Viewer({ item, index, total, close, previous, next }) {
  const closeButton = useRef(null);
  const touchStart = useRef(null);

  useEffect(() => {
    closeButton.current?.focus();
  }, []);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Förstorad bild från utfört arbete"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#121712]/95 px-12 pb-14 pt-16 sm:px-20"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      onTouchStart={(e) => { touchStart.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const distance = (e.changedTouches[0]?.clientX ?? touchStart.current) - touchStart.current;
        touchStart.current = null;
        if (distance < -65) next();
        if (distance > 65) previous();
      }}
    >
      <button ref={closeButton} type="button" onClick={close} aria-label="Stäng bildvisare" className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-white text-[#222] shadow-lg transition hover:bg-[#ece9e4]">
        <X size={23} aria-hidden="true" />
      </button>
      <button type="button" onClick={previous} aria-label="Föregående bild" className="absolute left-1 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-[#222] transition hover:bg-white sm:left-5">
        <ArrowLeft size={21} aria-hidden="true" />
      </button>
      {item.before ? (
        <div className="grid max-h-[72vh] max-w-[1100px] grid-cols-1 gap-5 overflow-y-auto overscroll-contain rounded-xl sm:grid-cols-2 sm:gap-4">
          {comparisonPhotos(item).map(({ src, label, alt }) => (
            <figure key={label} className="min-w-0">
              <figcaption className="pb-2 text-center text-sm font-semibold text-white">{label}</figcaption>
              <img src={src} alt={alt} {...imageVariants(item, src, "(min-width: 1260px) 540px, (min-width: 640px) 42vw, calc(100vw - 96px)")} decoding="async" className="w-full rounded-lg object-contain sm:max-h-[64vh]" />
            </figure>
          ))}
        </div>
      ) : (
        <img src={item.src} alt={item.title} className="max-h-[73vh] max-w-full rounded-lg object-contain" />
      )}
      <button type="button" onClick={next} aria-label="Nästa bild" className="absolute right-1 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-[#222] transition hover:bg-white sm:right-5">
        <ArrowRight size={21} aria-hidden="true" />
      </button>
      <div className="pointer-events-none absolute bottom-3 left-1/2 w-[90vw] -translate-x-1/2 text-center text-sm text-white">
        <span className="font-semibold">{item.title}</span>
        <span className="ml-2 text-white/70">{index + 1} / {total}</span>
      </div>
    </div>,
    document.body
  );
}

export const WorkGallery = () => {
  const [filter, setFilter] = useState("alla");
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(null);
  const opener = useRef(null);

  const filtered = useMemo(() => WORK.filter((item) => filter === "alla" || item.category === filter), [filter]);
  const preview = useMemo(() => {
    if (expanded) return filtered;
    return filter === "alla" ? filtered.filter((item) => item.featured) : filtered.slice(0, 6);
  }, [expanded, filter, filtered]);
  const needsExpand = filtered.length > preview.length || expanded;

  useEffect(() => {
    if (active === null) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") setActive(null);
      if (event.key === "ArrowRight") setActive((index) => (index + 1) % filtered.length);
      if (event.key === "ArrowLeft") setActive((index) => (index - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [active, filtered.length]);

  const closeViewer = () => {
    setActive(null);
    requestAnimationFrame(() => opener.current?.focus());
  };

  const chooseFilter = (id) => {
    setFilter(id);
    setExpanded(false);
    setActive(null);
  };

  return (
    <section id="arbeten" className="bg-white py-14 md:py-20" data-testid="work-gallery">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col items-start gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="eyebrow">Utförda arbeten</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Se våra arbeten</h2>
            <p className="mt-3 max-w-2xl text-[var(--ink-soft)]">Bilder från trädgårdsarbeten och städning. Klicka på en bild för att se mer.</p>
          </div>
          <div role="group" aria-label="Visa arbeten inom" className="flex flex-wrap gap-2 lg:pb-1">
            {FILTERS.map(({ id, label }) => (
              <button key={id} type="button" onClick={() => chooseFilter(id)} aria-pressed={filter === id}
                className={`min-h-11 rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${filter === id ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--line)] bg-white text-[var(--ink)] hover:bg-[var(--sand)]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6" data-testid="work-gallery-grid">
          {preview.map((item) => {
            const index = filtered.findIndex((entry) => entry.id === item.id);
            const categoryLabel = item.category === "stadning" ? "Städning" : "Trädgård";
            return (
              <button
                key={item.id}
                type="button"
                onClick={(event) => { opener.current = event.currentTarget; setActive(index); }}
                aria-label={`Förstora bild: ${item.title}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-white text-left shadow-[0_4px_18px_rgba(58,53,49,0.035)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_34px_rgba(58,53,49,0.10)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--red)]"
              >
                <div className="relative aspect-[3/2] w-full overflow-hidden bg-[var(--sand)]">
                  <Photo item={item} className="h-full w-full transition-transform duration-500 group-hover:scale-[1.035]" />
                  <span className="absolute right-3 top-3 rounded-full bg-white/95 p-2 text-[var(--ink)] shadow-sm opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true"><Images size={17} /></span>
                </div>
                <div className="flex min-h-[86px] w-full items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-[0.09em] text-[var(--red)]">{categoryLabel}{item.before ? " · Före och efter" : ""}</span>
                    <h3 className="mt-1 text-base font-bold leading-snug text-[var(--ink)]">{item.title}</h3>
                    {item.description && item.responsive && <p className="mt-1 text-sm leading-relaxed text-[var(--ink-soft)]">{item.description}</p>}
                  </div>
                  <ArrowUpRight size={20} className="shrink-0 text-[var(--ink-soft)] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </div>
              </button>
            );
          })}
        </div>

        {needsExpand && (
          <div className="mt-8 text-center">
            <button type="button" onClick={() => setExpanded((current) => !current)} aria-expanded={expanded} className="inline-flex min-h-12 items-center justify-center rounded-full border border-[var(--line)] bg-white px-7 py-3 text-sm font-semibold text-[var(--ink)] transition hover:bg-[var(--sand)]">
              {expanded ? "Visa färre bilder" : "Visa alla bilder"}
            </button>
          </div>
        )}
      </div>
      {active !== null && filtered[active] && (
        <Viewer item={filtered[active]} index={active} total={filtered.length} close={closeViewer}
          previous={() => setActive((index) => (index - 1 + filtered.length) % filtered.length)}
          next={() => setActive((index) => (index + 1) % filtered.length)} />
      )}
    </section>
  );
};
